import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, Smartphone, Lock, Database, Eye, Server } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Personvern() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <LanguageSwitcher currentLang={i18n.language} onChangeLang={changeLanguage} />
            <Link to="/" className="p-1 border-2 border-black rounded-md hover:bg-black/5 transition-colors">
              <img 
                src="https://customer-assets.emergentagent.com/job_mindairy-site/artifacts/6hwqvtsj_mindary%20logo.jpeg" 
                alt="mindary logo" 
                className="w-10 h-10 object-contain"
              />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 via-indigo-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 mb-6">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-foreground tracking-tight mb-4" data-testid="privacy-title">
              {t('privacy.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('privacy.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Main Privacy Card */}
            <Card className="bg-emerald-50 border-emerald-100">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center">
                    <Smartphone className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-semibold text-foreground">
                      {t('privacy.localStorage')}
                    </h2>
                    <p className="text-muted-foreground">{t('privacy.localStorageSubtitle')}</p>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t('privacy.localStorageDesc1')}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {t('privacy.localStorageDesc2')}
                </p>
              </CardContent>
            </Card>

            {/* Privacy Points */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-blue-50 border-blue-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center mb-4">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t('privacy.noSharing')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('privacy.noSharingDesc')}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-indigo-50 border-indigo-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center mb-4">
                    <Database className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t('privacy.youOwnData')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('privacy.youOwnDataDesc')}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-purple-50 border-purple-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center mb-4">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t('privacy.noTracking')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('privacy.noTrackingDesc')}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-teal-50 border-teal-100">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center mb-4">
                    <Server className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t('privacy.secureStorage')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('privacy.secureStorageDesc')}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact */}
            <div className="text-center pt-8">
              <p className="text-muted-foreground mb-4">
                {t('privacy.questions')}
              </p>
              <Link to="/kontakt" className="text-emerald-600 hover:text-emerald-700 font-medium">
                {t('privacy.contactUs')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
